import { PromptBlueprint } from "@/types";
import { PromptVersion } from "@/types/apiGetters";
import {
  CreateModelComparisonPresetRequest,
  PromptTemplateEngineOverride,
} from "@/queries/smart-tables/types";

export interface ModelComparisonSelection {
  id: string;
  label: string;
  engine: PromptTemplateEngineOverride;
}

export const normalizeLabel = (value: string): string => value.replace(/\s+/g, " ").trim();

export const dedupeModelLabels = (
  selections: ModelComparisonSelection[],
): CreateModelComparisonPresetRequest["models"] => {
  const counts = new Map<string, number>();

  return selections.map(selection => {
    const normalized =
      normalizeLabel(selection.label) || normalizeLabel(selection.engine.model) || "Model";
    const nextCount = (counts.get(normalized) ?? 0) + 1;
    counts.set(normalized, nextCount);

    return {
      label: nextCount === 1 ? normalized : `${normalized} ${nextCount}`,
      engine: selection.engine,
    };
  });
};

export const fallbackPromptData = (): Partial<PromptBlueprint> => ({
  metadata: {
    model: {
      provider: "openai",
      name: "gpt-4o",
      parameters: {},
      model_config_id: null,
      api_type: null,
    },
  },
  provider_base_url_name: null,
});

export const promptDataToEngineOverride = (
  promptData?: Partial<PromptBlueprint>,
): PromptTemplateEngineOverride => ({
  provider: promptData?.metadata?.model?.provider ?? null,
  model: promptData?.metadata?.model?.name ?? "",
  parameters: promptData?.metadata?.model?.parameters ?? {},
  model_config_id: promptData?.metadata?.model?.model_config_id ?? null,
  provider_id: promptData?.provider_id ?? null,
  provider_base_url_name: promptData?.provider_base_url_name ?? null,
  api_type: promptData?.metadata?.model?.api_type ?? null,
});

export const engineOverrideToPromptData = (
  engine: PromptTemplateEngineOverride,
): Partial<PromptBlueprint> => ({
  metadata: {
    model: {
      provider: engine.provider ?? "",
      name: engine.model,
      parameters: engine.parameters ?? {},
      model_config_id: engine.model_config_id ?? null,
      api_type: engine.api_type ?? null,
    },
  },
  provider_base_url_name: engine.provider_base_url_name ?? null,
  provider_id: engine.provider_id ?? undefined,
});

export const promptVersionToPromptData = (
  promptVersion?: PromptVersion,
): Partial<PromptBlueprint> | undefined => {
  const model = promptVersion?.metadata?.model;
  if (!model?.name) {
    return undefined;
  }

  return {
    metadata: {
      model: {
        provider: model.provider ?? "",
        name: model.name,
        parameters: model.parameters ?? {},
        model_config_id: model.model_config_id ?? null,
        api_type: model.api_type ?? null,
      },
    },
    provider_base_url_name: promptVersion?.provider_base_url_name ?? null,
    provider_id: promptVersion?.provider_id ?? undefined,
  };
};

export const defaultModelFromPromptVersion = (
  promptVersion?: PromptVersion,
): ModelComparisonSelection | null => {
  const promptData = promptVersionToPromptData(promptVersion);
  const modelName = promptData?.metadata?.model?.name;
  if (!modelName) {
    return null;
  }

  return {
    id: `default-${modelName}`,
    label: modelName,
    engine: promptDataToEngineOverride(promptData),
  };
};
